import React, { createContext, useContext, useEffect } from 'react';
import s from './styles.module.css.json';
import { generateStyleFromCtx } from '../generateStyleFromCtx';
import classNames from 'classnames';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export var CtxContext = createContext(null);
export function useCtx() {
    var ctx = useContext(CtxContext);
    if (!ctx) {
        throw new Error('useCtx requires <Canvas />!');
    }
    return ctx;
}
/**
 * @example Color palette CSS variables
 *
 * Within the `Canvas` component, a color palette is made available as a set of
 * CSS variables, allowing you to conform to the theme of the current environment:
 *
 * ```js
 * <Canvas ctx={ctx}>
 *   <Section title="Text colors">
 *     <table>
 *       <tbody>
 *         <tr>
 *           <td>
 *             <code>--base-body-color</code>
 *           </td>
 *           <td width="30%">
 *             <div
 *               style={{
 *                 width: '30px',
 *                 height: '30px',
 *                 background: 'var(--base-body-color)',
 *               }}
 *             />
 *           </td>
 *         </tr>
 *         <tr>
 *           <td>
 *             <code>--light-body-color</code>
 *           </td>
 *           <td width="30%">
 *             <div
 *               style={{
 *                 width: '30px',
 *                 height: '30px',
 *                 background: 'var(--light-body-color)',
 *               }}
 *             />
 *           </td>
 *         </tr>
 *         <tr>
 *           <td>
 *             <code>--placeholder-body-color</code>
 *           </td>
 *           <td width="30%">
 *             <div
 *               style={{
 *                 width: '30px',
 *                 height: '30px',
 *                 background: 'var(--placeholder-body-color)',
 *               }}
 *             />
 *           </td>
 *         </tr>
 *       </tbody>
 *     </table>
 *   </Section>
 *   <Section title="UI colors">
 *     <table>
 *       <tbody>
 *         <tr>
 *           <td>
 *             <code>--light-bg-color</code>
 *           </td>
 *           <td width="30%">
 *             <div
 *               style={{
 *                 width: '30px',
 *                 height: '30px',
 *                 background: 'var(--light-bg-color)',
 *               }}
 *             />
 *           </td>
 *         </tr>
 *         <tr>
 *           <td>
 *             <code>--lighter-bg-color</code>
 *           </td>
 *           <td width="30%">
 *             <div
 *               style={{
 *                 width: '30px',
 *                 height: '30px',
 *                 background: 'var(--lighter-bg-color)',
 *               }}
 *             />
 *           </td>
 *         </tr>
 *         <tr>
 *           <td>
 *             <code>--disabled-bg-color</code>
 *           </td>
 *           <td width="30%">
 *             <div
 *               style={{
 *                 width: '30px',
 *                 height: '30px',
 *                 background: 'var(--disabled-bg-color)',
 *               }}
 *             />
 *           </td>
 *         </tr>
 *         <tr>
 *           <td>
 *             <code>--border-color</code>
 *           </td>
 *           <td width="30%">
 *             <div
 *               style={{
 *                 width: '30px',
 *                 height: '30px',
 *                 background: 'var(--border-color)',
 *               }}
 *             />
 *           </td>
 *         </tr>
 *         <tr>
 *           <td>
 *             <code>--darker-border-color</code>
 *           </td>
 *           <td width="30%">
 *             <div
 *               style={{
 *                 width: '30px',
 *                 height: '30px',
 *                 background: 'var(--darker-border-color)',
 *               }}
 *             />
 *           </td>
 *         </tr>
 *         <tr>
 *           <td>
 *             <code>--alert-color</code>
 *           </td>
 *           <td width="30%">
 *             <div
 *               style={{
 *                 width: '30px',
 *                 height: '30px',
 *                 background: 'var(--alert-color)',
 *               }}
 *             />
 *           </td>
 *         </tr>
 *         <tr>
 *           <td>
 *             <code>--warning-color</code>
 *           </td>
 *           <td width="30%">
 *             <div
 *               style={{
 *                 width: '30px',
 *                 height: '30px',
 *                 background: 'var(--warning-color)',
 *               }}
 *             />
 *           </td>
 *         </tr>
 *         <tr>
 *           <td>
 *             <code>--notice-color</code>
 *           </td>
 *           <td width="30%">
 *             <div
 *               style={{
 *                 width: '30px',
 *                 height: '30px',
 *                 background: 'var(--notice-color)',
 *               }}
 *             />
 *           </td>
 *         </tr>
 *         <tr>
 *           <td>
 *             <code>--warning-bg-color</code>
 *           </td>
 *           <td width="30%">
 *             <div
 *               style={{
 *                 width: '30px',
 *                 height: '30px',
 *                 background: 'var(--warning-bg-color)',
 *               }}
 *             />
 *           </td>
 *         </tr>
 *         <tr>
 *           <td>
 *             <code>--add-color</code>
 *           </td>
 *           <td width="30%">
 *             <div
 *               style={{
 *                 width: '30px',
 *                 height: '30px',
 *                 background: 'var(--add-color)',
 *               }}
 *             />
 *           </td>
 *         </tr>
 *         <tr>
 *           <td>
 *             <code>--remove-color</code>
 *           </td>
 *           <td width="30%">
 *             <div
 *               style={{
 *                 width: '30px',
 *                 height: '30px',
 *                 background: 'var(--remove-color)',
 *               }}
 *             />
 *           </td>
 *         </tr>
 *       </tbody>
 *     </table>
 *   </Section>
 *   <Section title="Project-specific colors">
 *     <table>
 *       <tbody>
 *         <tr>
 *           <td>
 *             <code>--accent-color</code>
 *           </td>
 *           <td width="30%">
 *             <div
 *               style={{
 *                 width: '30px',
 *                 height: '30px',
 *                 background: 'var(--accent-color)',
 *               }}
 *             />
 *           </td>
 *         </tr>
 *         <tr>
 *           <td>
 *             <code>--primary-color</code>
 *           </td>
 *           <td width="30%">
 *             <div
 *               style={{
 *                 width: '30px',
 *                 height: '30px',
 *                 background: 'var(--primary-color)',
 *               }}
 *             />
 *           </td>
 *         </tr>
 *         <tr>
 *           <td>
 *             <code>--light-color</code>
 *           </td>
 *           <td width="30%">
 *             <div
 *               style={{
 *                 width: '30px',
 *                 height: '30px',
 *                 background: 'var(--light-color)',
 *               }}
 *             />
 *           </td>
 *         </tr>
 *         <tr>
 *           <td>
 *             <code>--dark-color</code>
 *           </td>
 *           <td width="30%">
 *             <div
 *               style={{
 *                 width: '30px',
 *                 height: '30px',
 *                 background: 'var(--dark-color)',
 *               }}
 *             />
 *           </td>
 *         </tr>
 *       </tbody>
 *     </table>
 *   </Section>
 * </Canvas>;
 * ```
 *
 * @example Typography CSS variables
 *
 * Typography is a foundational element in UI design. Good typography
 * establishes a strong, cohesive visual hierarchy and presents content clearly
 * and efficiently to users. Within the `Canvas` component, a set of CSS
 * variables is available allowing your plugin to conform to the overall
 * look&feel of DatoCMS:
 *
 * ```js
 * <Canvas ctx={ctx}>
 *   <table>
 *     <tbody>
 *       <tr>
 *         <td>
 *           <code>--font-size-xxs</code>
 *         </td>
 *         <td>
 *           <div style={{ fontSize: 'var(--font-size-xxs)' }}>
 *             Size XXS
 *           </div>
 *         </td>
 *       </tr>
 *       <tr>
 *         <td>
 *           <code>--font-size-xs</code>
 *         </td>
 *         <td>
 *           <div style={{ fontSize: 'var(--font-size-xs)' }}>Size XS</div>
 *         </td>
 *       </tr>
 *       <tr>
 *         <td>
 *           <code>--font-size-s</code>
 *         </td>
 *         <td>
 *           <div style={{ fontSize: 'var(--font-size-s)' }}>Size S</div>
 *         </td>
 *       </tr>
 *       <tr>
 *         <td>
 *           <code>--font-size-m</code>
 *         </td>
 *         <td>
 *           <div style={{ fontSize: 'var(--font-size-m)' }}>Size M</div>
 *         </td>
 *       </tr>
 *       <tr>
 *         <td>
 *           <code>--font-size-l</code>
 *         </td>
 *         <td>
 *           <div
 *             style={{
 *               fontSize: 'var(--font-size-l)',
 *               fontWeight: 'var(--font-weight-bold)',
 *             }}
 *           >
 *             Size L
 *           </div>
 *         </td>
 *       </tr>
 *       <tr>
 *         <td>
 *           <code>--font-size-xl</code>
 *         </td>
 *         <td>
 *           <div
 *             style={{
 *               fontSize: 'var(--font-size-xl)',
 *               fontWeight: 'var(--font-weight-bold)',
 *             }}
 *           >
 *             Size XL
 *           </div>
 *         </td>
 *       </tr>
 *       <tr>
 *         <td>
 *           <code>--font-size-xxl</code>
 *         </td>
 *         <td>
 *           <div
 *             style={{
 *               fontSize: 'var(--font-size-xxl)',
 *               fontWeight: 'var(--font-weight-bold)',
 *             }}
 *           >
 *             Size XXL
 *           </div>
 *         </td>
 *       </tr>
 *       <tr>
 *         <td>
 *           <code>--font-size-xxxl</code>
 *         </td>
 *         <td>
 *           <div
 *             style={{
 *               fontSize: 'var(--font-size-xxxl)',
 *               fontWeight: 'var(--font-weight-bold)',
 *             }}
 *           >
 *             Size XXXL
 *           </div>
 *         </td>
 *       </tr>
 *     </tbody>
 *   </table>
 * </Canvas>;
 * ```
 *
 * @example Spacing CSS variables
 *
 * The following CSS variables are available as well, to mimick the spacing
 * between elements used by the main DatoCMS application. Negative spacing
 * variables are available too (`--negative-spacing-<SIZE>`).
 *
 * ```js
 * <Canvas ctx={ctx}>
 *   <table>
 *     <tbody>
 *       <tr>
 *         <td>
 *           <code>--spacing-s</code>
 *         </td>
 *         <td>
 *           <div
 *             style={{
 *               background: 'var(--accent-color)',
 *               width: 'var(--spacing-s)',
 *               height: 'var(--spacing-s)',
 *             }}
 *           />
 *         </td>
 *       </tr>
 *       <tr>
 *         <td>
 *           <code>--spacing-m</code>
 *         </td>
 *         <td>
 *           <div
 *             style={{
 *               background: 'var(--accent-color)',
 *               width: 'var(--spacing-m)',
 *               height: 'var(--spacing-m)',
 *             }}
 *           />
 *         </td>
 *       </tr>
 *       <tr>
 *         <td>
 *           <code>--spacing-l</code>
 *         </td>
 *         <td>
 *           <div
 *             style={{
 *               background: 'var(--accent-color)',
 *               width: 'var(--spacing-l)',
 *               height: 'var(--spacing-l)',
 *             }}
 *           />
 *         </td>
 *       </tr>
 *       <tr>
 *         <td>
 *           <code>--spacing-xl</code>
 *         </td>
 *         <td>
 *           <div
 *             style={{
 *               background: 'var(--accent-color)',
 *               width: 'var(--spacing-xl)',
 *               height: 'var(--spacing-xl)',
 *             }}
 *           />
 *         </td>
 *       </tr>
 *       <tr>
 *         <td>
 *           <code>--spacing-xxl</code>
 *         </td>
 *         <td>
 *           <div
 *             style={{
 *               background: 'var(--accent-color)',
 *               width: 'var(--spacing-xxl)',
 *               height: 'var(--spacing-xxl)',
 *             }}
 *           />
 *         </td>
 *       </tr>
 *       <tr>
 *         <td>
 *           <code>--spacing-xxxl</code>
 *         </td>
 *         <td>
 *           <div
 *             style={{
 *               background: 'var(--accent-color)',
 *               width: 'var(--spacing-xxxl)',
 *               height: 'var(--spacing-xxxl)',
 *             }}
 *           />
 *         </td>
 *       </tr>
 *     </tbody>
 *   </table>
 * </Canvas>;
 * ```
 */
export function Canvas(_a) {
    var ctx = _a.ctx, children = _a.children, noAutoResizer = _a.noAutoResizer;
    var mode = ctx.mode;
    useEffect(function () {
        if (mode !== 'renderPage' && !noAutoResizer) {
            var ctxWithAutoResizer_1 = ctx;
            ctxWithAutoResizer_1.startAutoResizer();
            return function () {
                ctxWithAutoResizer_1.stopAutoResizer();
            };
        }
        return undefined;
    }, [mode, noAutoResizer]);
    return (React.createElement(CtxContext.Provider, { value: ctx },
        React.createElement("div", { className: classNames(s['themeVariables'], s['canvas']), style: generateStyleFromCtx(ctx) }, children)));
}
//# sourceMappingURL=index.js.map